import { CheckoutCompletedItem, CheckoutResponse } from '@checkout/util-models';
import {
  BasketItemQuoteDetails,
  calculatePaymentOptionAnalyticsValue,
} from '@domgen/dgx-fe-basket';
import {
  CustomerDetails,
  formatPurchaseDate,
  hasValue,
} from '@domgen/dgx-fe-common';
import { GAAnonymiseObject, GAEvent, GATag } from '@domgen/dgx-fe-ga';
import { CurrencyCode } from '@domgen/dgx-fe-i18n';
import { format } from 'date-fns';
import * as CheckoutActions from '../../+state/checkout.actions';

export function formatCheckoutTags(
  checkoutResponse: CheckoutResponse,
  currencyCode: CurrencyCode,
  customerDetails?: CustomerDetails
): GATag[] {
  return [
    formatCheckoutEcommerceGaTag(checkoutResponse, currencyCode),
    formatCheckoutPurchaseGaTag(checkoutResponse, currencyCode),
    formatCheckoutTransactionGaTag(checkoutResponse, customerDetails),
  ];
}

export function formatCheckoutEcommerceGaTag(
  { basket, completedItems }: CheckoutResponse,
  currencyCode: CurrencyCode
): GATag {
  const paymentOptions = completedItems.map(
    (completedItem: CheckoutCompletedItem) =>
      completedItem.item.data.quotes[0].paymentOptions[0]
  );
  const revenue: number = calculatePaymentOptionAnalyticsValue(
    ...paymentOptions
  );

  const products = completedItems.map(
    (completedItem: CheckoutCompletedItem) => {
      const applianceDetails = completedItem.item.data.applianceDetails;
      const confirmation = completedItem.confirmation;
      const quote = completedItem.item.data.quotes[0];
      const paymentOption = quote.paymentOptions[0];
      const offerDetails =
        'offerDetails' in completedItem.item.data
          ? completedItem.item.data.offerDetails
          : undefined;
      const price = calculatePaymentOptionAnalyticsValue(paymentOption);

      return {
        brand: applianceDetails.brandName,
        category: applianceDetails.applianceCategory,
        dimension100: applianceDetails.brandName,
        dimension24: paymentOption.paymentType,
        dimension65: applianceDetails.applianceName,
        dimension69: offerDetails?.offerReference ?? '',
        dimension70: quote.coverType,
        dimension71: quote.excessAmount,
        dimension111: applianceDetails.purchasePrice,
        dimension112: applianceDetails.inWarranty,
        dimension113: format(formatPurchaseDate(applianceDetails), 'MMMM yyyy'),
        id: confirmation.planNumber,
        metric15: price,
        name: `${applianceDetails.brandName} ${applianceDetails.applianceName}`,
        price,
        quantity: 1,
      };
    }
  );

  return {
    event: 'eecPurchase',
    ecommerce: {
      currency: currencyCode,
      purchase: {
        actionField: {
          action: 'purchase',
          id: basket.basketId,
          revenue,
        },
        products,
      },
    },
  };
}

export function formatCheckoutPurchaseGaTag(
  { basket, completedItems }: CheckoutResponse,
  currencyCode: CurrencyCode
): GATag {
  const paymentOptions = completedItems.map(
    (completedItem: CheckoutCompletedItem) =>
      completedItem.item.data.quotes[0].paymentOptions[0]
  );
  const value: number = calculatePaymentOptionAnalyticsValue(...paymentOptions);

  const items = completedItems.map((completedItem: CheckoutCompletedItem) => {
    const applianceDetails = completedItem.item.data.applianceDetails;
    const confirmation = completedItem.confirmation;
    const paymentOption = completedItem.item.data.quotes[0].paymentOptions[0];
    const price = calculatePaymentOptionAnalyticsValue(paymentOption);

    return {
      appliance_purchase_cost: applianceDetails.purchasePrice,
      appliance_purchase_date: format(
        formatPurchaseDate(applianceDetails),
        'MMMM yyyy'
      ),
      appliance_type: applianceDetails.applianceName,
      appliance_warranty: applianceDetails.inWarranty,
      item_brand: applianceDetails.brandName,
      item_category: applianceDetails.applianceCategory,
      item_id: confirmation.planNumber,
      item_name: `${applianceDetails.brandName} ${applianceDetails.applianceName}`,
      paymentType: paymentOption.paymentType,
      price,
      quantity: 1,
    };
  });

  return {
    event: 'purchase',
    ecommerce: {
      currency: currencyCode,
      value,
      transaction_id: basket.basketId,
      items,
    },
  };
}

export function formatCheckoutTransactionGaTag(
  { basket, completedItems }: CheckoutResponse,
  customerDetails?: CustomerDetails
): GATag {
  const planTerm = formatPlanTermFromQuote(
    completedItems[0].item.data.quotes[0]
  );

  const paymentOptions = completedItems.map(
    (completedItem: CheckoutCompletedItem) =>
      completedItem.item.data.quotes[0].paymentOptions[0]
  );
  const transactionTotal: number = calculatePaymentOptionAnalyticsValue(
    ...paymentOptions
  );

  const transactionProducts = completedItems.map(
    (completedItem: CheckoutCompletedItem) => {
      const applianceDetails = completedItem.item.data.applianceDetails;
      const confirmation = completedItem.confirmation;
      const paymentOption = completedItem.item.data.quotes[0].paymentOptions[0];
      const price = calculatePaymentOptionAnalyticsValue(paymentOption);

      return {
        category: applianceDetails.brandCode,
        name: applianceDetails.applianceName,
        price,
        quantity: 1,
        sku: confirmation.planNumber,
      };
    }
  );

  return {
    event: 'transaction',
    paymentType: paymentOptions[0].paymentType,
    planTerm,
    transactionId: basket.basketId,
    transactionProducts,
    transactionTotal,
    ...GAAnonymiseObject(customerDetails),
  };
}

export function formatCheckoutCustomerRiskTag(
  action:
    | ReturnType<typeof CheckoutActions.checkoutSuccess>
    | ReturnType<typeof CheckoutActions.checkoutFailure>
): GATag | undefined {
  if (
    'checkoutResponse' in action &&
    hasValue(action.checkoutResponse.weight)
  ) {
    return {
      event: GAEvent.GenericEvent,
      eventCategory: 'customer risk',
      eventAction: 'success',
      eventLabel: action.checkoutResponse.weight,
      customerRiskScore: action.checkoutResponse.weight,
    };
  }

  return;
}

export function formatPlanTermFromQuote(quote: BasketItemQuoteDetails): string {
  return quote.periodOfCover === 24 || quote.periodOfCover === 48
    ? `${quote.periodOfCover / 12} years`
    : 'monthly';
}
